import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHistory, useLocation } from 'react-router-dom'

import { useAppDispatch, useDomainQueryParams } from 'app/hooks'
import { commonMessages } from 'app/i18n/messages'
import ConfirmModal from 'components/Modal/ConfirmModal'

import { DeleteDomainFormContent } from './styles'
import { deleteDomainMessages } from './messages'
import { DOMAIN_API_ERROR } from 'features/jobs/consts'
import { deleteDomainThunk } from 'features/domains/slice'
import { DomainType } from 'features/domains/types'
import SuccessModal from 'components/Modal/SuccessModal'
import { createDomainQueryParams } from 'features/domains/utils'
import { getDomainSubDomainsThunk } from 'components/DomainBreadcrumbs/slice'

interface DeleteDomainModalProps {
  domain: DomainType
  onClose: () => void
}

const DeleteDomainModal = (props: DeleteDomainModalProps) => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const history = useHistory()
  const domainQueryParams = useDomainQueryParams()

  const [error, setError] = useState<null | string>(null)
  const [isLoading, setLoading] = useState(false)

  const { pathname } = location

  const parentDomainId = props.domain.parent?.id

  const onSubmit = async () => {
    setLoading(true)
    try {
      await dispatch(deleteDomainThunk(props.domain.id))
      if (parentDomainId) {
        await dispatch(
          getDomainSubDomainsThunk({
            domainId: parentDomainId,
            forceUpdate: true,
          })
        )
      }

      // Redirect to parent domain after deleting the nested one
      const newDomainQueryParams = createDomainQueryParams(
        domainQueryParams.slice(0, domainQueryParams.length - 1)
      )
      history.push(`${pathname}?${newDomainQueryParams}`)
      props.onClose()
    } catch (err: any) {
      console.log('Error deleting Domain/Group', err)

      if (err?.name === DOMAIN_API_ERROR.DOMAIN_HAS_NESTED_DOMAINS) {
        return setError(formatMessage(deleteDomainMessages.hasNestedError))
      }
      setError(formatMessage(commonMessages.commonError))
    } finally {
      setLoading(false)
    }
  }

  if (error) {
    return (
      <SuccessModal
        isOpen={!!error}
        title={formatMessage(commonMessages.error)}
        confirmBtnTitle={formatMessage(commonMessages.close)}
        onConfirm={() => setError(null)}
      >
        {error}
      </SuccessModal>
    )
  }

  return (
    <ConfirmModal
      isOpen={!error}
      onDiscard={props.onClose}
      title={
        <FormattedMessage
          {...deleteDomainMessages.title}
          values={{
            domainName: props.domain.name,
          }}
        />
      }
      discardBtnTitle={<FormattedMessage {...deleteDomainMessages.cancelBtn} />}
      confirmBtnTitle={<FormattedMessage {...deleteDomainMessages.ctaBtn} />}
      onConfirm={onSubmit}
      variant="primary"
      isConfirmBtnDisabled={isLoading}
    >
      <DeleteDomainFormContent>
        <FormattedMessage
          {...deleteDomainMessages.confirmText}
          values={{
            domainName: props.domain.name,
            br: <br />,
            parentDomainName: props.domain.parent?.name,
          }}
        />
      </DeleteDomainFormContent>
    </ConfirmModal>
  )
}

export default DeleteDomainModal
